/**
 * Cloud Spend Page
 * Main page for monitoring GCP and Twilio cloud spend
 * Uses tabbed interface for easy scalability as more providers are added
 */

import {
  Cloud,
  RefreshCw,
  Clock,
  AlertTriangle,
  Phone,
  LayoutGrid,
  Server,
  AudioLines,
} from 'lucide-react';
import { useCloudSpend } from '../hooks/useCloudSpend';
import { useTwilioSpend } from '../hooks/useTwilioSpend';
import { useAwsSpend } from '../hooks/useAwsSpend';
import { useDeepgramSpend } from '../hooks/useDeepgramSpend';
import { CombinedSummaryCard } from '../components/CombinedSummaryCard';
import { SpendSummaryCard } from '../components/SpendSummaryCard';
import { SpendByServiceChart } from '../components/SpendByServiceChart';
import { SpendByProjectChart } from '../components/SpendByProjectChart';
import { SpendTrendChart } from '../components/SpendTrendChart';
import { SpendDetailTable } from '../components/SpendDetailTable';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { DataStatus, ServiceSpendWithProvider } from '../types';

interface ProviderLoadState {
  name: string;
  dataStatus?: DataStatus;
  error: unknown;
}

interface ProviderIssue {
  name: string;
  title: string;
  description: string;
  severity: 'error' | 'mock';
}

export function CloudSpendPage() {
  const {
    data: gcpData,
    isLoading: gcpLoading,
    error: gcpError,
    refetch: refetchGcp,
    isFetching: gcpFetching,
  } = useCloudSpend();

  const {
    data: twilioData,
    isLoading: twilioLoading,
    error: twilioError,
    refetch: refetchTwilio,
    isFetching: twilioFetching,
  } = useTwilioSpend();

  const {
    data: awsData,
    isLoading: awsLoading,
    error: awsError,
    refetch: refetchAws,
    isFetching: awsFetching,
  } = useAwsSpend();

  const {
    data: deepgramData,
    isLoading: deepgramLoading,
    error: deepgramError,
    refetch: refetchDeepgram,
    isFetching: deepgramFetching,
  } = useDeepgramSpend();

  const isLoading = gcpLoading || twilioLoading || awsLoading || deepgramLoading;
  const isFetching = gcpFetching || twilioFetching || awsFetching || deepgramFetching;
  const verifiedGcpData = gcpData?.dataStatus === 'ready' ? gcpData : undefined;
  const verifiedTwilioData = twilioData?.dataStatus === 'ready' ? twilioData : undefined;
  const verifiedAwsData = awsData?.dataStatus === 'ready' ? awsData : undefined;
  const verifiedDeepgramData = deepgramData?.dataStatus === 'ready' ? deepgramData : undefined;

  const providerLoadStates: ProviderLoadState[] = [
    { name: 'GCP', dataStatus: gcpData?.dataStatus, error: gcpError },
    { name: 'Twilio', dataStatus: twilioData?.dataStatus, error: twilioError },
    { name: 'AWS', dataStatus: awsData?.dataStatus, error: awsError },
    { name: 'Deepgram', dataStatus: deepgramData?.dataStatus, error: deepgramError },
  ];
  const providerIssues = providerLoadStates.flatMap<ProviderIssue>(({ name, dataStatus, error: providerError }) => {
    if (providerError) {
      return [{
        name,
        title: `${name} billing unavailable`,
        description: `${providerError instanceof Error ? providerError.message : 'Provider request failed.'} No ${name} amount is included in totals, charts, or tables.`,
        severity: 'error',
      }];
    }
    if (dataStatus === 'mock') {
      return [{
        name,
        title: `${name} billing data hidden`,
        description: `The ${name} API returned sample data. It has been excluded from every total, chart, and table.`,
        severity: 'mock',
      }];
    }
    return [];
  });
  const pendingProviders = providerLoadStates.filter(
    ({ dataStatus, error: providerError }) => !providerError && dataStatus === 'pending',
  );

  const refetch = () => {
    refetchGcp();
    refetchTwilio();
    refetchAws();
    refetchDeepgram();
  };

  // Get the most recent lastUpdated from available data
  const lastUpdated = verifiedGcpData?.lastUpdated
    || verifiedTwilioData?.lastUpdated
    || verifiedAwsData?.lastUpdated
    || verifiedDeepgramData?.lastUpdated;

  // Build combined services for the detail table
  const combinedServices: ServiceSpendWithProvider[] = [
    ...(verifiedGcpData?.byService.map((s) => ({
      ...s,
      provider: 'gcp' as const,
      currency: verifiedGcpData.currency,
    })) || []),
    ...(verifiedTwilioData?.byService.map((s) => ({
      ...s,
      provider: 'twilio' as const,
      currency: verifiedTwilioData.currency,
    })) || []),
    ...(verifiedAwsData?.byService.map((s) => ({
      ...s,
      provider: 'aws' as const,
      currency: verifiedAwsData.currency,
    })) || []),
    ...(verifiedDeepgramData?.byService.map((s) => ({
      ...s,
      provider: 'deepgram' as const,
      currency: verifiedDeepgramData.currency,
    })) || []),
  ];

  // Build provider summaries for combined card
  const providerSummaries = [
    ...(verifiedGcpData
      ? [{ provider: 'gcp' as const, summary: verifiedGcpData.summary, currency: verifiedGcpData.currency }]
      : []),
    ...(verifiedTwilioData
      ? [
          {
            provider: 'twilio' as const,
            summary: verifiedTwilioData.summary,
            currency: verifiedTwilioData.currency,
          },
        ]
      : []),
    ...(verifiedAwsData
      ? [
          {
            provider: 'aws' as const,
            summary: verifiedAwsData.summary,
            currency: verifiedAwsData.currency,
          },
        ]
      : []),
    ...(verifiedDeepgramData
      ? [
          {
            provider: 'deepgram' as const,
            summary: verifiedDeepgramData.summary,
            currency: verifiedDeepgramData.currency,
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="animate-pulse">
            <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
            <div className="h-8 w-64 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                  <div className="h-8 w-32 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!gcpData && !twilioData && !awsData && !deepgramData && providerIssues.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Cloud className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cloud Spend Monitor</h1>
              <p className="text-sm text-gray-600 mt-1">
                Track and analyze cloud costs across all providers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Data Status Banners */}
        {providerIssues.map((issue) => (
          <Alert
            key={issue.name}
            className={issue.severity === 'error'
              ? 'bg-red-50 border-red-200'
              : 'bg-amber-50 border-amber-200'}
          >
            <AlertTriangle
              className={issue.severity === 'error' ? 'h-4 w-4 text-red-600' : 'h-4 w-4 text-amber-600'}
            />
            <AlertTitle className={issue.severity === 'error' ? 'text-red-800' : 'text-amber-800'}>
              {issue.title}
            </AlertTitle>
            <AlertDescription className={issue.severity === 'error' ? 'text-red-700' : 'text-amber-700'}>
              {issue.description}
            </AlertDescription>
          </Alert>
        ))}

        {pendingProviders.map(({ name }) => (
          <Alert key={name} className="bg-blue-50 border-blue-200">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Waiting for {name} billing data</AlertTitle>
            <AlertDescription className="text-blue-700">
              No verified {name} spend is available yet, so this provider is excluded from totals.
            </AlertDescription>
          </Alert>
        ))}

        {/* Combined Summary Card - Always visible */}
        {providerSummaries.length > 0 && <CombinedSummaryCard providers={providerSummaries} />}

        {/* Tabbed Provider Views */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="overview" className="gap-2">
              <LayoutGrid className="h-4 w-4" />
              Overview
            </TabsTrigger>
            {verifiedGcpData && (
              <TabsTrigger value="gcp" className="gap-2">
                <Cloud className="h-4 w-4" />
                GCP
              </TabsTrigger>
            )}
            {verifiedTwilioData && (
              <TabsTrigger value="twilio" className="gap-2">
                <Phone className="h-4 w-4" />
                Twilio
              </TabsTrigger>
            )}
            {verifiedAwsData && (
              <TabsTrigger value="aws" className="gap-2">
                <Server className="h-4 w-4" />
                AWS
              </TabsTrigger>
            )}
            {verifiedDeepgramData && (
              <TabsTrigger value="deepgram" className="gap-2">
                <AudioLines className="h-4 w-4" />
                Deepgram
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Combined Detail Table */}
            {combinedServices.length > 0 && (
              <SpendDetailTable
                data={combinedServices}
                currency={providerSummaries[0]?.currency || 'CAD'}
                showProvider
              />
            )}
          </TabsContent>

          {/* GCP Tab */}
          {verifiedGcpData && (
            <TabsContent value="gcp" className="space-y-6">
              <SpendSummaryCard summary={verifiedGcpData.summary} currency={verifiedGcpData.currency} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SpendByServiceChart data={verifiedGcpData.byService} currency={verifiedGcpData.currency} />
                <SpendByProjectChart data={verifiedGcpData.byProject} currency={verifiedGcpData.currency} />
              </div>
              <SpendTrendChart data={verifiedGcpData.dailyTrend} currency={verifiedGcpData.currency} />
              <SpendDetailTable data={verifiedGcpData.byService} currency={verifiedGcpData.currency} />
            </TabsContent>
          )}

          {/* Twilio Tab */}
          {verifiedTwilioData && (
            <TabsContent value="twilio" className="space-y-6">
              <SpendSummaryCard summary={verifiedTwilioData.summary} currency={verifiedTwilioData.currency} />
              <SpendByServiceChart data={verifiedTwilioData.byService} currency={verifiedTwilioData.currency} />
              <SpendTrendChart data={verifiedTwilioData.dailyTrend} currency={verifiedTwilioData.currency} />
              <SpendDetailTable data={verifiedTwilioData.byService} currency={verifiedTwilioData.currency} />
            </TabsContent>
          )}

          {/* AWS Tab */}
          {verifiedAwsData && (
            <TabsContent value="aws" className="space-y-6">
              <SpendSummaryCard summary={verifiedAwsData.summary} currency={verifiedAwsData.currency} />
              <SpendByServiceChart data={verifiedAwsData.byService} currency={verifiedAwsData.currency} />
              <SpendTrendChart data={verifiedAwsData.dailyTrend} currency={verifiedAwsData.currency} />
              <SpendDetailTable data={verifiedAwsData.byService} currency={verifiedAwsData.currency} />
            </TabsContent>
          )}

          {/* Deepgram Tab */}
          {verifiedDeepgramData && (
            <TabsContent value="deepgram" className="space-y-6">
              <SpendSummaryCard summary={verifiedDeepgramData.summary} currency={verifiedDeepgramData.currency} />
              <SpendByServiceChart data={verifiedDeepgramData.byService} currency={verifiedDeepgramData.currency} />
              <SpendTrendChart data={verifiedDeepgramData.dailyTrend} currency={verifiedDeepgramData.currency} />
              <SpendDetailTable data={verifiedDeepgramData.byService} currency={verifiedDeepgramData.currency} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
